// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Portfolio insights — the data flywheel. Pure functions over loaded project
// states: extract estimate-vs-awarded pairs from buyout data and aggregate
// cross-project accuracy by CSI division. No React, no DB — fully testable.
//
// The headline this powers: "Across your last 12 awards you estimate
// Electrical 8% high and Concrete 5% low." No competitor closes this loop,
// because none owns the estimate → bid → buyout arc in one tool.

import { ALL_DIVISIONS } from "../projectData";
import type { BuyoutItem } from "../types";

// Awarded statuses — only signed/awarded work has a real contract number to
// compare against the estimate. "pending"/"bidding" are still in flight.
const AWARDED_STATUSES = new Set(["awarded", "executed"]);

// Variance within ±this fraction is "on the money" — neither high nor low.
const ACCURATE_BAND = 0.02;

export interface AwardPair {
  projectId: string;
  projectName: string;
  scope: string;
  divisionId: string;
  vendor: string;
  budget: number;     // estimate's budgetAmount for the scope
  contract: number;   // awarded contractAmount
  variance: number;   // contract - budget  (positive ⇒ came in OVER budget)
  variancePct: number; // variance / budget
  contractDate: string;
}

export interface DivisionAccuracy {
  divisionId: string;
  code: string;
  name: string;
  awardCount: number;
  totalBudget: number;
  totalContract: number;
  meanVariancePct: number;     // simple mean of per-award pct (each award equal)
  medianVariancePct: number;   // robust to outliers
  weightedVariancePct: number; // Σvariance / Σbudget — reflects dollar impact
  // Interpreted from weightedVariancePct:
  //   contract < budget ⇒ estimate ran HIGH (over-budgeted)
  //   contract > budget ⇒ estimate ran LOW  (under-budgeted)
  bias: "high" | "low" | "accurate";
}

export interface PortfolioAccuracy {
  totalAwards: number;
  pairsWithoutBudget: number;  // awarded rows skipped because no budget was set
  totalBudget: number;
  totalContract: number;
  overallVariancePct: number;  // Σvariance / Σbudget across everything
  divisions: DivisionAccuracy[]; // sorted by |weightedVariancePct| desc
}

const DIV_LOOKUP: Record<string, { code: string; name: string }> = Object.fromEntries(
  ALL_DIVISIONS.map((d) => [d.id, { code: d.code, name: d.name }]),
);

/** Extract awarded estimate-vs-contract pairs from one project's buyout log. */
export function extractAwardPairs(project: {
  id: string;
  name: string;
  buyoutItems?: BuyoutItem[] | null;
}): AwardPair[] {
  const out: AwardPair[] = [];
  for (const b of project.buyoutItems || []) {
    if (!AWARDED_STATUSES.has(b.status)) continue;
    const budget = Number(b.budgetAmount) || 0;
    const contract = Number(b.contractAmount) || 0;
    if (contract <= 0) continue; // no awarded amount yet → nothing to compare
    const variance = contract - budget;
    out.push({
      projectId: project.id,
      projectName: project.name,
      scope: b.scope || "",
      divisionId: b.divisionId || "",
      vendor: b.vendor || "",
      budget,
      contract,
      variance,
      variancePct: budget > 0 ? variance / budget : 0,
      contractDate: b.contractDate || "",
    });
  }
  return out;
}

function median(nums: number[]): number {
  if (!nums.length) return 0;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function biasOf(weightedPct: number): DivisionAccuracy["bias"] {
  if (weightedPct > ACCURATE_BAND) return "low";   // contract over budget ⇒ under-estimated
  if (weightedPct < -ACCURATE_BAND) return "high"; // contract under budget ⇒ over-estimated
  return "accurate";
}

/** Aggregate award pairs into per-division and portfolio-wide accuracy. */
export function aggregateAccuracy(pairs: AwardPair[]): PortfolioAccuracy {
  // Only pairs with a real budget can produce a meaningful variance %.
  const withBudget = pairs.filter((p) => p.budget > 0);
  const pairsWithoutBudget = pairs.length - withBudget.length;

  const byDiv = new Map<string, AwardPair[]>();
  for (const p of withBudget) {
    const key = p.divisionId || "unknown";
    (byDiv.get(key) ?? byDiv.set(key, []).get(key)!).push(p);
  }

  const divisions: DivisionAccuracy[] = [];
  for (const [divisionId, group] of byDiv) {
    const totalBudget = group.reduce((s, p) => s + p.budget, 0);
    const totalContract = group.reduce((s, p) => s + p.contract, 0);
    const pcts = group.map((p) => p.variancePct);
    const meanVariancePct = pcts.reduce((s, v) => s + v, 0) / pcts.length;
    const weightedVariancePct = totalBudget > 0 ? (totalContract - totalBudget) / totalBudget : 0;
    const meta = DIV_LOOKUP[divisionId] ?? { code: divisionId, name: divisionId || "Unassigned" };
    divisions.push({
      divisionId,
      code: meta.code,
      name: meta.name,
      awardCount: group.length,
      totalBudget,
      totalContract,
      meanVariancePct,
      medianVariancePct: median(pcts),
      weightedVariancePct,
      bias: biasOf(weightedVariancePct),
    });
  }

  // Biggest dollar-weighted swings first — that's where the estimating
  // process most needs attention.
  divisions.sort((a, b) => Math.abs(b.weightedVariancePct) - Math.abs(a.weightedVariancePct));

  const totalBudget = withBudget.reduce((s, p) => s + p.budget, 0);
  const totalContract = withBudget.reduce((s, p) => s + p.contract, 0);

  return {
    totalAwards: withBudget.length,
    pairsWithoutBudget,
    totalBudget,
    totalContract,
    overallVariancePct: totalBudget > 0 ? (totalContract - totalBudget) / totalBudget : 0,
    divisions,
  };
}

/** Convenience: run extraction + aggregation across many loaded projects. */
export function computePortfolioAccuracy(
  projects: { id: string; name: string; buyoutItems?: BuyoutItem[] | null }[],
): PortfolioAccuracy {
  const pairs = projects.flatMap((p) => extractAwardPairs(p));
  return aggregateAccuracy(pairs);
}
